import { useEffect, useMemo, useState } from 'react';
import { ApiRequest } from '@/utils/axios';
import { inventoryStore } from '@/stores/inventory.store';
import { modalStore } from '@/stores/modal.store';
import { coinflipStore, coinflipIntent } from '@/stores/coinflip.store';
import useUserStore from '@/stores/user.store';
import { ECoinflipSide, ICoinflipGame } from '@/utils/types/ICoinflipGame';
import { itemsValue } from '@/utils/itemInfo';
import { notify } from '@/utils/notify';
import AlertIcon from '@/components/Notifications/AlertIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import CoinflipInventoryPicker from '@/components/Modals/Inventory/CoinflipInventoryPicker';
import CoinflipJoinFooter from '@/components/Modals/Inventory/CoinflipJoinFooter';

interface Props {
    id: number;
}

const JoinSteamCoinflip = ({ id }: Props) => {
    const {
        steamItems,
        selectedItemIds,
        maxSelectedItems,
        setSelectedById,
        clearInventory,
        loadSteamInventory,
        uncacheSteamInventory,
    } = inventoryStore((state) => state);
    const { activeGames, games } = coinflipStore((state) => state);
    const { user } = useUserStore((state) => state);
    const { setTitle, setComponent, setData, toggleModal } = modalStore((state) => state);

    const [loading, setLoading] = useState(true);
    const [coinflip, setCoinflip] = useState<ICoinflipGame>();

    const joinSide =
        coinflip?.firstSide === ECoinflipSide.FIRST ? ECoinflipSide.SECOND : ECoinflipSide.FIRST;

    async function load(force = false) {
        const {
            data: {
                response: {
                    global: { appid },
                },
            },
        } = await ApiRequest.get('/settings/list');
        if (force) await uncacheSteamInventory(appid, 2);
        await loadSteamInventory(appid, 2);
        setLoading(false);
    }

    useEffect(() => {
        setCoinflip(activeGames().find((g) => g?.id === id) ?? games.get(id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, games]);

    useEffect(() => {
        load();
        return () => clearInventory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (coinflip?.secondUser && coinflip.secondUser.id !== user.id) {
            toggleModal(false);
            notify(
                'error',
                <AlertIcon />,
                <NotifyWrapper title="Error" text="This coinflip is started" />
            );
        }
        if (coinflip?.pendingJoin && coinflip.pendingJoin.user.id !== user.id) {
            toggleModal(false);
            notify(
                'error',
                <AlertIcon />,
                <NotifyWrapper title="Error" text="Another player is joining this coinflip" />
            );
        }
    }, [coinflip?.secondUser, coinflip?.pendingJoin, user.id]);

    const opponentValue = useMemo(
        () => (coinflip ? itemsValue(coinflip.firstItems) : 0),
        [coinflip]
    );
    const range: [number, number] = useMemo(
        () => [Number((opponentValue * 0.9).toFixed(2)), Number((opponentValue * 1.1).toFixed(2))],
        [opponentValue]
    );

    const pickerItems = steamItems.map((it) => ({
        id: BigInt(it.assetId),
        name: it.name,
        priceUsd: it.priceUsd,
        colorHex: it.colorHex,
        icon: it.icon,
        iconBackground: it.iconBackground,
    }));
    const inventoryTotal = pickerItems.reduce((s, i) => s + i.priceUsd, 0);
    const selected = pickerItems.filter((i) => selectedItemIds.has(i.id));
    const selectedAmount = selected.reduce((s, i) => s + i.priceUsd, 0);
    const inRange = selectedAmount >= range[0] && selectedAmount <= range[1];
    const chance = selectedAmount > 0 ? (selectedAmount / (selectedAmount + opponentValue)) * 100 : 0;
    const canJoin = selected.length > 0 && inRange;

    async function join() {
        if (!canJoin || !coinflip) return;
        const assetIds = Array.from(selectedItemIds).map((i) => i.toString());
        const gameId = Number(coinflip.id);
        coinflipIntent.joinIds.add(gameId);
        try {
            const {
                data: {
                    success,
                    response: { offerId },
                },
            } = await ApiRequest.post('/coinflip/join/steam', {
                assetIds,
                side: joinSide,
                gameId,
            });
            if (success && coinflipIntent.joinIds.has(gameId)) {
                setTitle('CONFIRM TRADE');
                setComponent('Steam trade');
                setData({ tradeId: offerId, coinflipGameId: gameId });
            }
        } catch (error) {
            coinflipIntent.joinIds.delete(gameId);
            throw error;
        }
    }

    if (!coinflip) return null;

    return (
        <CoinflipInventoryPicker
            items={pickerItems}
            selectedItemIds={selectedItemIds}
            setSelectedById={setSelectedById}
            maxItems={maxSelectedItems}
            loading={loading}
            onRefresh={() => {
                setLoading(true);
                clearInventory();
                load(true);
            }}
            footer={
                <CoinflipJoinFooter
                    inventoryTotal={inventoryTotal}
                    selectedCount={selected.length}
                    selectedAmount={selectedAmount}
                    range={range}
                    chance={chance}
                    onAction={join}
                    actionLabel="JOIN"
                    actionDisabled={!canJoin}
                />
            }
        />
    );
};

export default JoinSteamCoinflip;
